import React, { useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Loader } from '@mantine/core';
import { MdChevronRight, MdClose, MdPersonAddAlt, MdRefresh, MdSearch } from 'react-icons/md';
import Avatar, { AVATAR_SIZES } from '../../atoms/Avatar';
import Input from '../../atoms/Input';
import { pluralize } from '../../lib/pluralize';
import { ACCOUNTS_INLINE_LIMIT, ACCOUNTS_INLINE_RECENT } from '../AccountSwitch/accountsList';
import { sameAccount, type AccountId, type IBrowserAccount } from '../AccountSwitch/accountsPort';
import { UserMenuSeparator } from './UserMenuParts';
import './UserMenuAccounts.scss';

export interface IUserMenuAccountsProps {
    accounts: IBrowserAccount[];
    /** Щелчок по строке: переключиться (или войти заново — у `expired`) */
    onSelect: (account: IBrowserAccount) => void;
    /** «Убрать из списка» у строки; без обработчика действия нет */
    onRemove?: (account: IBrowserAccount) => void;
    /** «Добавить учётную запись»; без обработчика пункта нет (предел) */
    onAdd?: () => void;
    /** «Повторить» загрузку списка после отказа */
    onRetry?: () => void;
    /** Учётная запись, в которую идёт переключение: у неё ход, остальные действия заблокированы */
    switchingId?: AccountId | null;
    /** Учётная запись, которая убирается из списка */
    removingId?: AccountId | null;
    /** Идёт «Добавить учётную запись» (парковка текущей) */
    addLoading?: boolean;
    /** Список грузится: без строк — заглушка, со строками — прежний список до ответа */
    listLoading?: boolean;
    /** Отказ загрузки или действия — строкой под списком */
    error?: string;
}

/** С какого числа записей экран «Учётные записи» показывает поиск */
const ACCOUNTS_SEARCH_FROM = 6;

interface IUserMenuAccountsViewProps extends IUserMenuAccountsProps {
    /** Корень меню: «Ещё N учётных записей» открывает экран со всем списком (`UserMenuDialog`) */
    onShowAll?: () => void;
    showAllRef?: React.Ref<HTMLButtonElement>;
    /** id экрана со всем списком — `aria-controls` строки «Ещё N» */
    showAllControls?: string;
    /** Экран «Учётные записи»: весь список, поиск по ФИО и логину, без разделителя сверху */
    screen?: boolean;
}

const searchKey = (text: string) => text.toLocaleLowerCase('ru').replaceAll('ё', 'е');

/**
 * Учётные записи браузера в меню профиля (мультиаккаунт): остальные учётные записи связки, переключение одним
 * щелчком, «Добавить учётную запись» и «Убрать из списка». Только вид: список, переключение и ошибки ведёт
 * `useUserMenuAccounts`. Строка в работе остаётся в фокусе (`aria-disabled`), после «Убрать» фокус — на соседней.
 *
 * Корень меню не растёт со связкой: до `ACCOUNTS_INLINE_LIMIT` записей видны все, дальше — две недавние (сервер
 * отдаёт список от недавней) и «Ещё N учётных записей» на экран со всем списком и поиском.
 */
const UserMenuAccounts: React.FC<IUserMenuAccountsViewProps> = ({
    accounts,
    onSelect,
    onRemove,
    onAdd,
    onRetry,
    switchingId = null,
    removingId = null,
    addLoading = false,
    listLoading = false,
    error,
    onShowAll,
    showAllRef,
    showAllControls,
    screen = false,
}) => {
    const busy = switchingId !== null || removingId !== null || addLoading;
    const rootRef = useRef<HTMLDivElement>(null);
    const removedAt = useRef<{ id: AccountId; index: number } | null>(null);
    const [query, setQuery] = useState('');
    const searchable = screen && accounts.length >= ACCOUNTS_SEARCH_FROM;
    const collapsed = !!onShowAll && accounts.length > ACCOUNTS_INLINE_LIMIT;

    const shown = useMemo(() => {
        if (collapsed) return accounts.slice(0, ACCOUNTS_INLINE_RECENT);
        const needle = searchable ? searchKey(query.trim()) : '';
        if (!needle) return accounts;
        return accounts.filter((account) =>
            searchKey(`${account.displayName ?? ''} ${account.login}`).includes(needle),
        );
    }, [accounts, collapsed, searchable, query]);
    const hidden = collapsed ? accounts.slice(ACCOUNTS_INLINE_RECENT) : [];

    useLayoutEffect(() => {
        const removed = removedAt.current;
        if (!removed || accounts.some((account) => sameAccount(account.userId, removed.id))) return;
        removedAt.current = null;
        const rows = rootRef.current?.querySelectorAll<HTMLElement>('[data-account-row], [data-account-add]');
        const next = rows?.[Math.min(removed.index, rows.length - 1)];
        if (next) next.focus();
        else rootRef.current?.focus();
    }, [accounts]);

    return (
        <div
            ref={rootRef}
            className="rf-user-menu-accounts"
            role="group"
            aria-label="Другие учётные записи"
            data-testid="user-menu-accounts"
            aria-busy={busy || listLoading}
            tabIndex={-1}
        >
            {!screen && <UserMenuSeparator />}
            {searchable && (
                <div className="rf-user-menu-accounts__search">
                    <Input
                        size="sm"
                        value={query}
                        onValueChange={setQuery}
                        onClear={() => setQuery('')}
                        placeholder="ФИО или логин"
                        aria-label="Поиск учётной записи"
                        leftSection={<MdSearch size="18" aria-hidden="true" />}
                        data-testid="user-menu-accounts-search"
                    />
                </div>
            )}
            {listLoading && accounts.length === 0 && (
                <div className="rf-user-menu-accounts__status" data-testid="user-menu-accounts-loading">
                    <Loader size={16} color="var(--primary-60-main)" />
                    <span>Загрузка учётных записей…</span>
                </div>
            )}
            {searchable && shown.length === 0 && (
                <div className="rf-user-menu-accounts__status" data-testid="user-menu-accounts-not-found">
                    Не найдено
                </div>
            )}
            {shown.map((account, index) => {
                const name = account.displayName || account.login;
                const switching = sameAccount(switchingId, account.userId);
                const removing = sameAccount(removingId, account.userId);
                const inWork = switching || removing;
                return (
                    <div
                        key={account.userId}
                        className="rf-user-menu-accounts__item"
                        data-testid={`user-menu-account-${account.userId}`}
                        data-switching={switching || undefined}
                    >
                        <button
                            type="button"
                            className="rf-user-menu-dialog__row rf-user-menu-accounts__row"
                            data-account-row=""
                            disabled={busy && !inWork}
                            aria-disabled={inWork || undefined}
                            aria-busy={inWork || undefined}
                            onClick={() => {
                                if (!busy) onSelect(account);
                            }}
                        >
                            <span className="rf-user-menu-accounts__avatar" aria-hidden="true">
                                <Avatar name={name} size={AVATAR_SIZES.m} />
                            </span>
                            <span className="rf-user-menu-accounts__text">
                                <span className="rf-user-menu-accounts__name">{name}</span>
                                <span className="rf-user-menu-accounts__login">{account.login}</span>
                                {account.expired && (
                                    <span className="rf-user-menu-accounts__expired">Требуется вход</span>
                                )}
                            </span>
                            {switching && (
                                <span className="rf-user-menu-accounts__progress">
                                    <Loader size={16} color="currentColor" />
                                    <span>Переключение…</span>
                                </span>
                            )}
                        </button>
                        {onRemove && !switching && (
                            <button
                                type="button"
                                className="rf-user-menu-accounts__remove"
                                aria-label={`Убрать из списка: ${name}`}
                                title="Убрать из списка"
                                data-testid={`user-menu-account-remove-${account.userId}`}
                                disabled={busy && !removing}
                                aria-disabled={removing || undefined}
                                aria-busy={removing || undefined}
                                data-removing={removing || undefined}
                                onClick={() => {
                                    if (busy) return;
                                    removedAt.current = { id: account.userId, index };
                                    onRemove(account);
                                }}
                            >
                                {removing ? (
                                    <Loader size={16} color="currentColor" />
                                ) : (
                                    <MdClose size="18" aria-hidden="true" />
                                )}
                            </button>
                        )}
                    </div>
                );
            })}
            {collapsed && (
                <button
                    type="button"
                    ref={showAllRef}
                    className="rf-user-menu-dialog__row rf-user-menu-accounts__more"
                    data-testid="user-menu-accounts-more"
                    aria-haspopup="menu"
                    aria-controls={showAllControls}
                    disabled={busy}
                    onClick={onShowAll}
                >
                    <span className="rf-user-menu-accounts__stack" aria-hidden="true">
                        {hidden.slice(0, 3).map((account) => (
                            <Avatar
                                key={account.userId}
                                name={account.displayName || account.login}
                                size={AVATAR_SIZES.xs}
                            />
                        ))}
                    </span>
                    <span className="rf-user-menu-dialog__row-text">
                        Ещё {hidden.length}{' '}
                        {pluralize(hidden.length, ['учётная запись', 'учётные записи', 'учётных записей'])}
                        <span className="rf-user-menu-dialog__row-hint rf-user-menu-accounts__more-hint">
                            {hidden.map((account) => account.displayName || account.login).join(', ')}
                        </span>
                    </span>
                    <MdChevronRight size="20" className="rf-user-menu-dialog__row-chevron" aria-hidden="true" />
                </button>
            )}
            {onAdd && (
                <button
                    type="button"
                    className="rf-user-menu-dialog__row rf-user-menu-accounts__add"
                    data-account-add=""
                    data-testid="user-menu-account-add"
                    disabled={busy && !addLoading}
                    aria-disabled={addLoading || undefined}
                    aria-busy={addLoading || undefined}
                    onClick={() => {
                        if (!busy) onAdd();
                    }}
                >
                    {addLoading ? <Loader size={24} color="currentColor" /> : <MdPersonAddAlt size="24" />}
                    <span className="rf-user-menu-dialog__row-text">Добавить учётную запись</span>
                </button>
            )}
            {error && (
                <div className="rf-user-menu-accounts__error" role="alert">
                    <span>{error}</span>
                    {onRetry && (
                        <button
                            type="button"
                            className="rf-user-menu-accounts__retry"
                            data-testid="user-menu-accounts-retry"
                            onClick={onRetry}
                        >
                            <MdRefresh size="16" aria-hidden="true" />
                            Повторить
                        </button>
                    )}
                </div>
            )}
        </div>
    );
};

export default UserMenuAccounts;
